import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { LAYOUT_ACTIONS, LayoutActions } from '@core/store/types';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import {
  firstRouteQueryParam,
  navigationItemIdQueryParam,
  openAppQueryParam
} from '@/app/platform/platform-core/user/constants';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';

@Injectable({
  providedIn: 'root'
})
export class FirstRouteGuard  {
  constructor(
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    private readonly router: Router,
    @Inject(LAYOUT_ACTIONS)
    private readonly layoutActions: LayoutActions
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.handle(route, state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.handle(childRoute, state);
  }

  handle(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (!route.queryParamMap.get(firstRouteQueryParam)) {
      return true;
    }

    const apps = this.userQueries.getAvailableApps();

    if (this.hasNoAccessToAnyApp(apps)) {
      return this.navigateToRoot();
    }

    if (this.hasAccessToExactlyOneApp(apps)) {
      const navigationItem = apps.find(
        a => a.rootNavigationItem.isBaseAccessAllowed
      ).rootNavigationItem;
      const routerLink = navigationItem.clone().routerLink;
      return this.router.createUrlTree([routerLink], {
        queryParams: {
          [openAppQueryParam]: true,
          [navigationItemIdQueryParam]: navigationItem.id
        }
      });
    }

    this.layoutActions.showAppMenu();
    return this.navigateToRoot();
  }

  private hasNoAccessToAnyApp(apps: UserEgiApp[]): boolean {
    return apps.every(a => !a.rootNavigationItem.isBaseAccessAllowed);
  }

  private hasAccessToExactlyOneApp(apps: UserEgiApp[]): boolean {
    return apps.filter(a => a.rootNavigationItem.isBaseAccessAllowed).length === 1;
  }

  private navigateToRoot(): UrlTree {
    return this.router.createUrlTree(['/']);
  }
}
